import {
  Box,
  Theme,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Menu,
  MenuItem,
  ButtonBase,
  ListSubheader,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useId, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GVExpansionPanel } from 'components/common';
import { GVTypography } from 'components/lib';
import { getStyleVariables } from 'styles/vars';
import { rgbToColor } from 'utils/colorUtils';
import { inspection, getFocusedDifference, getDifferencePanelOpen } from 'store';
import { updateDifference } from 'store/request/differences/actions';
import { DifferenceTypeNames } from 'types';
import { ClosestColor } from 'store/request/differences/types';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { FLOATING_PANEL_Z_INDEX, useFloatingPanel } from './useFloatingPanel';

const rgbObjectToColor = (rgb: { red: number; green: number; blue: number }): string =>
  `rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})`;

const PANEL_WIDTH = 367;

const useStyles = makeStyles()((theme: Theme) => {
  const styleVars = getStyleVariables(theme);
  return {
    root: {
      backgroundColor: styleVars.colors.menuLightGrey,
      width: `${PANEL_WIDTH}px`,
      cursor: 'default',
      '&.drag-handle': {
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
      },
      '& .no-drag': {
        cursor: 'default',
        pointerEvents: 'auto',
      },
    },
    expansionSummaryContent: {
      margin: 0,
    },
    expansionSummaryRoot: {
      margin: theme.spacing(0, 0, 1, 0),
      minHeight: '0px',
      '&.Mui-expanded': { minHeight: '0px' },
      padding: theme.spacing(0, 1),
      '& .MuiAccordionSummary-expandIconWrapper': {
        cursor: 'pointer',
        pointerEvents: 'auto',
      },
    },
    expandIcon: {
      height: '15px',
      width: '15px',
      margin: 0,
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
    },
    rndContainer: {
      cursor: 'default',
      '& > *': {
        width: '100% !important',
        minWidth: '100% !important',
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      },
      '& .drag-handle': {
        cursor: 'grab !important',
        '&:active': { cursor: 'grabbing !important' },
      },
      '& .no-drag': { cursor: 'default !important' },
      '&.dragging': {
        cursor: 'grabbing !important',
        '& .drag-handle': { cursor: 'grabbing !important' },
        '& .no-drag': { cursor: 'default !important' },
      },
    },
    headerRow: {
      display: 'flex',
      gap: theme.spacing(3),
      alignItems: 'stretch',
      padding: theme.spacing(1, 0),
    },
    headerColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: theme.spacing(0.5),
    },
    colorSwatch: {
      width: '16px',
      height: '16px',
      borderRadius: '4px',
      border: '1px solid rgba(255, 255, 255, 0.20)',
      flexShrink: 0,
    },
    swatchRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    sectionLabel: {
      fontSize: '10px',
      fontWeight: 600,
      color: 'var(--gds-color-text-supportive)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    accordionDetails: {
      flexDirection: 'column',
      padding: 0,
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
    },
    scrollContent: {
      ...styleVars.scrollBar,
      scrollbarGutter: 'auto',
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingBottom: '8px',
    },
    colorSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    colorRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '24px',
      alignSelf: 'stretch',
    },
    colorValue: {
      flex: 1,
      padding: '8px',
      borderRadius: '4px',
      backgroundColor: 'var(--gds-color-cool-gray-700)',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '13px',
      lineHeight: '22px',
    },
    separationValue: {
      display: 'flex',
      padding: '8px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: 1,
      borderRadius: '4px',
      backgroundColor: 'var(--gds-color-cool-gray-700)',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '13px',
      lineHeight: '22px',
    },
    dropdownTrigger: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
      padding: '4px 8px',
      borderRadius: '4px',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      cursor: 'pointer',
      background: 'none',
      font: 'inherit',
      color: 'inherit',
      textAlign: 'left',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
    },
    dropdownChevron: {
      fontSize: '18px',
      color: 'rgba(255, 255, 255, 0.5)',
      marginLeft: 'auto',
    },
    menuHeader: {
      fontSize: '10px',
      fontWeight: 600,
      color: 'var(--gds-color-text-supportive)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '8px 16px 4px',
      pointerEvents: 'none',
      lineHeight: 1.2,
      backgroundColor: 'transparent',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: '6px 16px',
      fontSize: '14px',
    },
    menuSwatch: {
      width: '14px',
      height: '14px',
      borderRadius: '3px',
      border: '1px solid rgba(255, 255, 255, 0.20)',
      flexShrink: 0,
    },
  };
});

interface ColorData {
  rgb: string;
  cmyk: string;
  lab: string;
}

const formatColorValue = (value: string): React.ReactNode => {
  const parts = value.split(/([A-Z]:)/g);
  return parts.map((part, i) => {
    if (/^[A-Z]:$/.test(part)) {
      return (
        <span key={i}>
          <span style={{ color: 'white', fontWeight: 700 }}>{part[0]}</span>:
        </span>
      );
    }
    return part.replace(/:/g, ':  ').replace(/,/g, ',  ');
  });
};

export interface ColorLayoutProps {
  id?: string;
}

const COLOR_FIELDS = [
  { key: 'rgb' as const, label: 'RGB' },
  { key: 'cmyk' as const, label: 'CMYK' },
  { key: 'lab' as const, label: 'LAB' },
];

const ColorLayout = ({ id }: ColorLayoutProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const isOpen = useSelector(getDifferencePanelOpen);
  const currentDifference = useSelector(getFocusedDifference);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const digitalApproximationMenuTriggerId = useId();
  const digitalApproximationMenuListId = useId();

  useEffect(() => {
    setMenuAnchorEl(null);
  }, [currentDifference?.id, isOpen]);

  const {
    documentsPanelElement,
    documentsPanelHeight,
    headerRef,
    rndRef,
    position,
    size,
    isDragging,
    isMounted,
    rndHandlers,
    rndStyle,
    rndClassName,
  } = useFloatingPanel({
    panelWidth: PANEL_WIDTH,
    isOpen,
    dragStyleId: 'color-drag-style',
    currentDifferenceId: currentDifference?.id,
    rndContainerClassName: classes.rndContainer,
    onAutoOpen: () => dispatch(inspection.actions.setDifferencePanelOpen(true)),
  });

  // --- Color data logic ---

  const isColorDifference = currentDifference?.type === DifferenceTypeNames.Color;
  const rawColorData = isColorDifference ? currentDifference?.colorDetails : null;

  const mapColorData = (prefix: 'sampled' | 'closest'): ColorData => ({
    rgb: rawColorData?.[`${prefix}Rgb`] ?? '',
    cmyk: rawColorData?.[`${prefix}Cmyk`] ?? '',
    lab: rawColorData?.[`${prefix}Lab`] ?? '',
  });

  // --- Build panel content ---

  const buildPanelContent = () => {
    if (!rawColorData) return null;

    const sampledColor = mapColorData('sampled');
    const closestColor = mapColorData('closest');
    const closestColors: ClosestColor[] = rawColorData.closestColors ?? [];
    const selectedIndex = rawColorData.selectedClosestColorIndex ?? 0;
    const selectedClosest = closestColors[selectedIndex];
    const closestColorName = selectedClosest?.color?.name ?? '';
    const separationName = rawColorData.separationName ?? '';
    const sourcePageNumber = currentDifference?.source?.location?.pageNumber ?? 1;
    const hasMultipleColors = closestColors.length > 1;

    const handleColorSelect = (index: number) => {
      setMenuAnchorEl(null);
      if (currentDifference && index !== selectedIndex) {
        dispatch(updateDifference(currentDifference.id, { selectedClosestColorIndex: index }));
      }
    };

    return (
      <GVExpansionPanel
        className={classes.root}
        id={id}
        data-testid="color-details-panel"
        expanded={isOpen}
        TransitionProps={{ timeout: isMounted ? undefined : 0 }}
        sx={{
          width: `${PANEL_WIDTH}px`,
          height: '100%',
          padding: '8px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          ...(!isMounted ? { transition: 'none !important' } : {}),
        }}
      >
        <Box ref={headerRef} flexDirection="column" width="100%" className="drag-handle">
          <AccordionSummary
            style={{ padding: 0 }}
            classes={{ content: classes.expansionSummaryContent }}
            className={classes.expansionSummaryRoot}
            id="ColorExpansionPanel"
            expandIcon={
              <ExpandLessIcon
                color="primary"
                id="ColorButton"
                className={`${classes.expandIcon} no-drag`}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(inspection.actions.setDifferencePanelOpen(!isOpen));
                }}
                style={{ cursor: 'pointer' }}
              />
            }
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest('.no-drag')) {
                dispatch(inspection.actions.setDifferencePanelOpen(!isOpen));
              }
            }}
          >
            <GVTypography emphasis="superhigh" variant="subtitle1">
              Color Details
            </GVTypography>
          </AccordionSummary>

          {separationName && (
            <>
              <Box className={classes.colorSection} sx={{ mt: 2, gap: 0.5 }}>
                <GVTypography className={classes.sectionLabel}>ASSOCIATED SEPARATION</GVTypography>

                <Box className={classes.colorRow}>
                  <Box className={classes.separationValue}>{separationName}</Box>
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mt: 1, mb: 1 }} />
            </>
          )}

          <Box className={classes.headerRow}>
            <Box className={classes.headerColumn}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Box className={classes.swatchRow}>
                  <Box className={classes.colorSwatch} style={{ backgroundColor: rgbToColor(sampledColor.rgb) }} />
                </Box>
              </Box>
              <GVTypography className={classes.sectionLabel}>{`SELECTED (P${sourcePageNumber})`}</GVTypography>
            </Box>
            <Box className={classes.headerColumn}>
              {hasMultipleColors ? (
                <>
                  <ButtonBase
                    id={digitalApproximationMenuTriggerId}
                    data-testid="digital-approximation-dropdown"
                    type="button"
                    disableRipple
                    className={`${classes.dropdownTrigger} no-drag`}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(menuAnchorEl)}
                    aria-controls={digitalApproximationMenuListId}
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  >
                    <Box className={classes.colorSwatch} style={{ backgroundColor: rgbToColor(closestColor.rgb) }} />
                    <GVTypography emphasis="superhigh" variant="body2">
                      {closestColorName}
                    </GVTypography>
                    <KeyboardArrowDownIcon className={classes.dropdownChevron} />
                  </ButtonBase>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => setMenuAnchorEl(null)}
                    sx={{ zIndex: FLOATING_PANEL_Z_INDEX + 1 }}
                    MenuListProps={{
                      id: digitalApproximationMenuListId,
                      'aria-labelledby': digitalApproximationMenuTriggerId,
                    }}
                    PaperProps={{
                      sx: {
                        backgroundColor: '#2A2D35',
                        color: 'white',
                        minWidth: '180px',
                        borderRadius: '8px',
                        mt: 0.5,
                      },
                    }}
                  >
                    <ListSubheader disableSticky className={classes.menuHeader}>
                      Similar Pantone Colors
                    </ListSubheader>
                    {closestColors.map((cc, idx) => (
                      <MenuItem
                        key={idx}
                        className={classes.menuItem}
                        selected={idx === selectedIndex}
                        onClick={() => handleColorSelect(idx)}
                      >
                        <Box
                          className={classes.menuSwatch}
                          style={{ backgroundColor: rgbObjectToColor(cc.color.rgb) }}
                        />
                        {cc.color.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                <Box className={classes.swatchRow}>
                  <Box className={classes.colorSwatch} style={{ backgroundColor: rgbToColor(closestColor.rgb) }} />
                  <GVTypography emphasis="superhigh" variant="body2">
                    {closestColorName}
                  </GVTypography>
                </Box>
              )}
              <GVTypography className={classes.sectionLabel}>DIGITAL APPROXIMATION</GVTypography>
            </Box>
          </Box>
        </Box>

        <AccordionDetails className={`${classes.accordionDetails} no-drag`}>
          <Box
            className={classes.scrollContent}
            sx={{
              maxHeight: documentsPanelHeight
                ? `${Math.max(100, documentsPanelHeight - (headerRef.current?.offsetHeight ?? 80) - 40)}px`
                : undefined,
            }}
          >
            {COLOR_FIELDS.map(({ key, label }) => (
              <Box key={key} className={classes.colorSection}>
                <Box className={classes.colorRow}>
                  <Box sx={{ flex: 1 }}>
                    <GVTypography className={classes.sectionLabel}>{label}</GVTypography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <GVTypography className={classes.sectionLabel}>{label}</GVTypography>
                  </Box>
                </Box>
                <Box className={classes.colorRow}>
                  <Box className={classes.colorValue}>{formatColorValue(sampledColor[key])}</Box>
                  <Box className={classes.colorValue}>{formatColorValue(closestColor[key])}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </GVExpansionPanel>
    );
  };

  const panelContent = buildPanelContent();
  if (!panelContent) return null;

  const content = (
    <Rnd
      ref={rndRef}
      position={position}
      size={size}
      bounds="window"
      {...rndHandlers}
      style={rndStyle}
      className={rndClassName}
      dragHandleClassName={isDragging ? 'no-drag' : 'drag-handle'}
      enableUserSelectHack={true}
      enableResizing={false}
      cancel=".no-drag"
    >
      {panelContent}
    </Rnd>
  );

  if (documentsPanelElement) return createPortal(content, documentsPanelElement);
  return null;
};

export default ColorLayout;
